import React, { useEffect, useState } from "react";
import { Lock, GraduationCap, Menu, X } from "lucide-react";
import { DARK, GOLD } from "../constants/theme";
import { NAV } from "../constants/navigation";
import { MasterclassReader } from "./MasterclassReader";
import { logEvent } from "../utils/analytics";

// Group NAV items by group for the locked sidebar.
function buildSections() {
  const main = NAV.filter(n => n.group === "main" && !n.parent);
  const pd   = NAV.filter(n => n.parent === "pd");
  const team = NAV.filter(n => n.parent === "team");
  const content = NAV.filter(n => n.group === "content");
  const toolbox = NAV.filter(n => n.group === "toolbox");
  return [
    { label: null, items: main },
    { label: "Player Development", items: pd },
    { label: "Team Coaching", items: team },
    { label: null, items: content },
    { label: "Toolbox", items: toolbox },
  ];
}

// Single source of truth for the "mobile" cutover used across the
// public preview. Matches the 768px threshold the LandingPage uses.
function useIsMobile(breakpoint = 768) {
  const [m, setM] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setM(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return m;
}

function LockedSidebar({ C, isMobile, onClose }) {
  const sections = buildSections();
  const onLockedClick = () => {
    logEvent("public_preview_sidebar_click", {});
    if (onClose) onClose();
    window.location.hash = "#/signup";
  };
  const width = isMobile ? "min(280px, 85vw)" : 240;
  return (
    <div style={{width,flexShrink:0,height:"100%",background:"#080808",borderRight:`1px solid rgba(255,255,255,0.04)`,display:"flex",flexDirection:"column",position:"relative"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:1,background:`linear-gradient(90deg,${GOLD}66,${GOLD}11,transparent)`}}/>
      {/* Header */}
      <div style={{padding:"22px 20px 14px",borderBottom:`1px solid rgba(255,255,255,0.05)`,display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:27,height:34,borderRadius:6,background:`${GOLD}18`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:GOLD}}>B</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:800,color:GOLD,letterSpacing:.5,lineHeight:1.1}}>BY ANY MEANS</div>
          <div style={{fontSize:9,color:"#555",letterSpacing:2,marginTop:3,fontWeight:600,textTransform:"uppercase"}}>Coaches Platform</div>
        </div>
        {isMobile && (
          <button onClick={onClose} aria-label="Close menu"
            style={{background:"transparent",border:"none",cursor:"pointer",padding:8,marginRight:-4,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:8,minWidth:44,minHeight:44}}>
            <X size={18} color={C.textMid}/>
          </button>
        )}
      </div>
      {/* Sections */}
      <div style={{flex:1,padding:"4px 0",overflowY:"auto"}}>
        {sections.map((section, si) => (
          <div key={si}>
            {section.label && (
              <div style={{padding:"16px 20px 8px"}}>
                <div style={{height:1,background:"rgba(255,255,255,0.05)",marginBottom:14}}/>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.28)",letterSpacing:2,fontWeight:700,textTransform:"uppercase"}}>
                  {section.label}
                </div>
              </div>
            )}
            {section.items.map(item => (
              <div key={item.id} onClick={onLockedClick}
                title="Sign up to unlock"
                style={{padding:"10px 20px",margin:"1px 8px",borderRadius:8,fontSize:13,
                  color:"#555",fontWeight:500,
                  display:"flex",alignItems:"center",gap:11,
                  cursor:"pointer",transition:"background .15s",
                  minHeight: isMobile ? 44 : undefined}}
                onMouseEnter={(e)=>{e.currentTarget.style.background="rgba(255,255,255,0.03)";}}
                onMouseLeave={(e)=>{e.currentTarget.style.background="transparent";}}>
                <item.Icon size={16} color="#444" strokeWidth={1.6} style={{opacity:.6}}/>
                <span style={{flex:1}}>{item.label}</span>
                <Lock size={11} color="#666"/>
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* Bottom CTA */}
      <div style={{padding:"14px 16px",borderTop:`1px solid rgba(255,255,255,0.05)`}}>
        <button onClick={()=>{logEvent("public_preview_sidebar_signup",{});if(onClose)onClose();window.location.hash="#/signup";}}
          style={{width:"100%",padding:"12px 0",borderRadius:9,background:GOLD,color:"#111",fontSize:12.5,fontWeight:800,border:"none",cursor:"pointer",boxShadow:`0 4px 14px ${GOLD}44`,minHeight:44}}>
          Sign up to unlock
        </button>
        <button onClick={()=>{logEvent("public_preview_sidebar_readmore",{});if(onClose)onClose();window.location.hash="#/landing";}}
          style={{width:"100%",marginTop:8,padding:"10px 0",borderRadius:9,background:"transparent",color:C.textMid,fontSize:12,fontWeight:700,border:`1px solid ${C.border}`,cursor:"pointer",minHeight:44}}>
          Read more
        </button>
      </div>
    </div>
  );
}

export function PublicPreview({ module }) {
  const C = DARK;
  // The drawer state and its two effects stood here, for a mobile nav
  // drawer that no longer exists. LockedSidebar and useIsMobile are kept
  // above for whatever wants them next.

  if (!module) {
    return (
      <div style={{minHeight:"100dvh",background:C.bg,color:C.text,display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'DM Sans',sans-serif"}}>
        <div style={{textAlign:"center"}}>
          <GraduationCap size={40} color={GOLD} style={{marginBottom:16,opacity:.6}}/>
          <div style={{fontSize:18,fontWeight:800,marginBottom:8}}>Masterclass not found</div>
          <div style={{fontSize:13,color:C.textMid,marginBottom:22}}>This free preview link isn't valid.</div>
          <button onClick={()=>{window.location.hash="#/landing";}}
            style={{padding:"10px 22px",borderRadius:10,background:GOLD,color:"#111",fontSize:13,fontWeight:800,border:"none",cursor:"pointer"}}>
            Go to landing page
          </button>
        </div>
      </div>
    );
  }

  return (
    /* No sidebar. A locked nav listing fourteen places a reader cannot go is
       a wall of padlocks in front of a free article, and on a phone it cost
       a hamburger in the header to open a drawer of things that all do the
       same thing: bounce you to signup. The one ask lives in the sticky bar
       at the bottom of the reader instead.

       The LockedSidebar component is kept above rather than deleted: the
       signup landing surfaces may well want it, and it is the only thing
       that renders NAV in a locked state. */
    /* 100dvh, not 100vh.
       On iOS Safari 100vh is the LARGE viewport, the height the page has
       when the browser's own toolbar is hidden. So a container set to 100vh
       extends underneath that toolbar, and anything pinned to its bottom
       sits behind it: the sign-up bar showed as a sliver of gold under
       Safari's address bar, which is what "gold at bottom messed up" was.
       dvh is the viewport as it actually is right now, and it updates as the
       toolbar slides away. The 100vh line stays first as the fallback for
       anything that does not know dvh. */
    <div style={{fontFamily:"'DM Sans',sans-serif",background:C.bg,height:"100dvh",display:"flex",color:C.text,overflow:"hidden"}}>
      <MasterclassReader
        module={module}
        C={C}
        dark={true}
        publicMode={true}
      />
    </div>
  );
}

export default PublicPreview;
